/**
 * Media recorder component - Handles photo and audio capture
 */

import { LitElement, html, css, svg } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';

const cameraIcon = svg`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`;

const micIcon = svg`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`;

@customElement('media-recorder')
export class MediaRecorder extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .container {
      display: flex;
      gap: 0.25rem;
      align-items: center;
    }

    .button {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.5rem 0.75rem;
      min-height: 44px;
      background-color: var(--bg-tertiary);
      color: var(--text-primary);
      border: 1px solid var(--color-border);
      border-radius: 0;
      font-size: 0.85rem;
      cursor: pointer;
      transition: none;
      box-sizing: border-box;
    }

    .button:hover {
      background-color: var(--bg-secondary);
    }

    .button.recording {
      background-color: var(--color-error);
      color: white;
    }

    input[type="file"] {
      display: none;
    }
  `;

  @property({ type: String }) type: 'photo' | 'audio' = 'photo';
  @property({ type: String }) label = '';

  @state() private isRecording: boolean = false;

  private mediaRecorder: globalThis.MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private fileInput: HTMLInputElement | null = null;

  private handlePhotoClick() {
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.accept = 'image/*';
      this.fileInput.capture = 'environment';
      this.fileInput.addEventListener('change', (e) => this.handlePhotoCapture(e));
    }
    // Reset so the same file can be selected again
    this.fileInput.value = '';
    this.fileInput.click();
  }

  private async handlePhotoCapture(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Compress and convert to JPEG
    const compressed = await this.compressImage(file);

    this.dispatchEvent(new CustomEvent('media-captured', {
      detail: { blob: compressed, type: 'photo', contentType: 'image/jpeg' },
      bubbles: true,
      composed: true
    }));
  }

  private async compressImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if larger than 1920x1080
        const maxWidth = 1920;
        const maxHeight = 1080;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          0.8
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  private async handleAudioToggle() {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }

  private async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      this.mediaRecorder = new globalThis.MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.audioChunks, { type: 'audio/webm' });

        this.dispatchEvent(new CustomEvent('media-captured', {
          detail: { blob, type: 'audio', contentType: 'audio/webm' },
          bubbles: true,
          composed: true
        }));

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      this.mediaRecorder.start();
      this.isRecording = true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to access microphone. Please grant permission.');
    }
  }

  private stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  render() {
    return html`
      <div class="container">
        ${this.type === 'photo' ? this.renderPhotoCapture() : this.renderAudioCapture()}
      </div>
    `;
  }

  private renderPhotoCapture() {
    return html`
      <button class="button" type="button" @click=${this.handlePhotoClick}>
        ${cameraIcon}
        ${this.label || 'photo'}
      </button>
    `;
  }

  private renderAudioCapture() {
    return html`
      <button
        class="button ${this.isRecording ? 'recording' : ''}"
        type="button"
        @click=${this.handleAudioToggle}
      >
        ${micIcon}
        ${this.isRecording
          ? 'stop'
          : (this.label || 'audio')}
      </button>
    `;
  }
}
